import React, { useEffect, useMemo, useCallback, useState } from "react";
import { Layout, theme, Grid, Button, Tooltip, Modal, message, Typography, Space, DatePicker, Upload } from "antd";
import { Outlet } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useAppContext } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import {
  getThemeSettings,
  getStoredThemeSettings,
  saveStoredThemeSettings,
} from "../services/themeService";
import { permissionService } from "../services/permissionService";
import ClientNavigation from "../navigation/ClientNavigationContainer";
import PageLoader from "../components/PageLoader";
import { LogoutOutlined, DownloadOutlined, FileTextOutlined, CheckCircleOutlined, UploadOutlined } from "@ant-design/icons";
// import NotificationPanel from "../components/NotificationPanel";
// import { NotificationProvider, useNotifications } from "../context/NotificationContext";
import axiosInstance from "../config/axios";
import dayjs from "dayjs";
import usePagePermission from "../hooks/usePagePermission";

const { useBreakpoint } = Grid;
const { Text } = Typography;

interface InsuranceDocument {
  id: string;
  document_name: string;
  party_id: string;
  created_at: string;
  metadata_id: string;
  filename: string;
  original_filename: string;
  file_extension: string;
  filesize: number;
  filemimetype: string;
  object_name: string;
}

const ClientLayoutInner: React.FC = () => {
  const {
    state: { isNavMenuClose },
  } = useAppContext();
  const { theme: currentTheme, updateTheme } = useTheme();
  const { user, logout } = useAuth();
  // const { unreadCount, refetchNotifications } = useNotifications();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isDarkMode = currentTheme.algorithm?.[0] === theme.darkAlgorithm;
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  // const [notificationPanelVisible, setNotificationPanelVisible] = useState(false);
  const [partyName, setPartyName] = useState<string>("");
  const [insuranceEndDate, setInsuranceEndDate] = useState<string>("");
  const [insuranceDocuments, setInsuranceDocuments] = useState<InsuranceDocument[]>([]);
  const [loadingInsurance, setLoadingInsurance] = useState(false);
  const [partyId, setPartyId] = useState<string>("");
  const [insuranceExpiringSoon, setInsuranceExpiringSoon] = useState(false);
  const [renewModalVisible, setRenewModalVisible] = useState(false);
  const [renewDate, setRenewDate] = useState<dayjs.Dayjs | null>(null);
  const [renewFiles, setRenewFiles] = useState<any[]>([]);
  const [renewLoading, setRenewLoading] = useState(false);

  const hasInsuranceReadPermission = usePagePermission('insurance-details', 'read');
  const hasInsuranceWritePermission = usePagePermission('insurance-details', 'write');  
  const canViewInsurance = user?.isclient || hasInsuranceReadPermission;  
  const canAccessDocuments = user?.isclient || hasInsuranceWritePermission;

  useEffect(() => {
    const storedPartyName = localStorage.getItem("selectedPartyName");
    const storedPartyId = localStorage.getItem("selectedPartyId");
    if (storedPartyName) {
      setPartyName(storedPartyName);
    }
    if (storedPartyId) {
      setPartyId(storedPartyId);
    }
  }, []);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "selectedPartyName") {
        setPartyName(event.newValue || "");
      }
      if (event.key === "selectedPartyId") {
        setPartyId(event.newValue || "");
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const themeTokens = useMemo(
    () => ({
      background: isDarkMode ? "#141414" : "#f5f7fa",
      contentBackground: isDarkMode ? "#1f1f1f" : "#ffffff",
      textColor: isDarkMode
        ? "rgba(255, 255, 255, 0.85)"
        : "rgba(0, 0, 0, 0.85)",
      textColorSecondary: isDarkMode
        ? "rgba(255, 255, 255, 0.45)"
        : "rgba(0, 0, 0, 0.45)",
      borderColor: isDarkMode ? "#303030" : "#f0f0f0",
      primaryColor: currentTheme.token?.colorPrimary || "#1d8348",
    }),
    [isDarkMode, currentTheme.token?.colorPrimary]
  );

  const loadThemeFromAPI = useCallback(async () => {
    try {
      if (getStoredThemeSettings()) {
        return;
      }

      const apiSettings = await getThemeSettings();
      if (apiSettings) {
        saveStoredThemeSettings(apiSettings);
        updateTheme(apiSettings);
      }
    } catch (error) {
      console.error("Failed to load theme settings in MainLayout:", error);
    }
  }, [updateTheme]);

  const loadStagePermissions = useCallback(async () => {
    const partyId = localStorage.getItem("selectedPartyId");
    const stageId = localStorage.getItem("selectedStageId");
    if (!partyId || !stageId || !user?.id) {
      console.debug(
        "Cannot load stage permissions: no party or stage selected"
      );
      setPermissionsLoading(false);
      return;
    }

    try {
      setPermissionsLoading(true);
      await permissionService.refreshStagePermissions(
        stageId,
        user.id || "",
        partyId
      );
    } catch (error) {
      console.error("Failed to load stage permissions in ClientLayout:", error);
    } finally {
      setPermissionsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!getStoredThemeSettings()) {
      loadThemeFromAPI();
    }
  }, [loadThemeFromAPI]);

  useEffect(() => {
    if (!user?.isclient) {
      loadStagePermissions();
    }
  }, [loadStagePermissions, user?.isclient]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "selectedPartyId") {
        loadStagePermissions();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [loadStagePermissions]);

  // Notification fetch disabled
  // useEffect(() => {
  //   const intervalId = window.setInterval(() => {
  //     refetchNotifications();
  //   }, 10000);

  //   return () => {
  //     window.clearInterval(intervalId);
  //   };
  // }, [refetchNotifications]);

  // Notification handlers disabled
  // const handleNotificationClick = () => {
  //   setNotificationPanelVisible(true);
  // };

  // const handleNotificationPanelClose = () => {
  //   setNotificationPanelVisible(false);
  // };

  const fetchInsuranceData = useCallback(async () => {
    if (!partyId || !canViewInsurance) return;

    try {
      setLoadingInsurance(true);
      
      const datesResponse = await axiosInstance.get(`/oandm/insurance/dates/${partyId}`);
      if (datesResponse.data.success && datesResponse.data.data) {
        const endDate = datesResponse.data.data.insurance_end_date;
        if (endDate) {
          setInsuranceEndDate(dayjs(endDate).format("DD MMM YYYY"));
          const now = dayjs();
          const end = dayjs(endDate);
          const daysRemaining = end.diff(now, 'day');
          setInsuranceExpiringSoon(daysRemaining <= 30);
        }
        
        if (user?.isclient && datesResponse.data.data.name) {
          setPartyName(datesResponse.data.data.name);
          localStorage.setItem("selectedPartyName", datesResponse.data.data.name);
        }
      }

      const docsResponse = await axiosInstance.get(`/oandm/insurance/documents/${partyId}`);
      if (docsResponse.data.success && docsResponse.data.data) {
        setInsuranceDocuments(docsResponse.data.data);
      }
    } catch (error: any) {
      console.error("Failed to fetch insurance data:", error);
    } finally {
      setLoadingInsurance(false);
    }
  }, [partyId, canViewInsurance, user?.isclient]);

  useEffect(() => {
    if (partyId) {
      fetchInsuranceData();
    }
  }, [partyId, fetchInsuranceData]);

  const handleInsuranceDownload = async () => {
    if (loadingInsurance) return;

    if (!canAccessDocuments) {
      message.warning("You don't have permission to download insurance documents");
      return;
    }

    if (insuranceDocuments.length === 0) {
      message.warning("No insurance documents available");
      return;
    }

    if (insuranceDocuments.length === 1) {
      const doc = insuranceDocuments[0];
      try {
        const response = await axiosInstance.get(
          `/oandm/insurance/download/${partyId}/${doc.id}`,
          {
            responseType: 'blob'
          }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', doc.original_filename || doc.filename || 'insurance_document');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
      } catch (error: any) {
        console.error("Failed to download document:", error);
        message.error("Failed to download document");
      }
    } else {
      showInsuranceDocumentsModal();
    }
  };

  const showInsuranceDocumentsModal = () => {
    if (insuranceDocuments.length === 0) {
      Modal.info({
        title: 'No Documents Available',
        content: 'There are no insurance documents uploaded for this party yet.',
        okText: 'Close',
        centered: true,
        icon: <FileTextOutlined style={{ color: themeTokens.primaryColor }} />,
      });
      return;
    }

    const getFileIcon = (extension: string) => {
      const ext = extension?.toLowerCase() || '';
      if (['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(ext)) return '🖼️';
      if (['pdf'].includes(ext)) return '📄';
      if (['doc', 'docx'].includes(ext)) return '📝';
      if (['xls', 'xlsx'].includes(ext)) return '📊';
      if (['zip', 'rar'].includes(ext)) return '📦';
      return '📎';
    };

    const getFileColor = (extension: string) => {
      const ext = extension?.toLowerCase() || '';
      if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return '#52c41a';
      if (['pdf'].includes(ext)) return '#f5222d';
      if (['doc', 'docx'].includes(ext)) return '#1890ff';
      if (['xls', 'xlsx'].includes(ext)) return '#13c2c2';
      return '#8c8c8c';
    };

    const handleDownload = async (doc: InsuranceDocument) => {
      if (!canAccessDocuments) {
        message.warning("You don't have permission to download documents");
        return;
      }
      
      try {
        const response = await axiosInstance.get(
          `/oandm/insurance/download/${partyId}/${doc.id}`,
          {
            responseType: 'blob'
          }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', doc.original_filename || doc.filename || 'insurance_document');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        message.success("Document downloaded successfully");
      } catch (error: any) {
        console.error("Failed to download document:", error);
        message.error("Failed to download document");
      }
    };

    const handlePreview = async (doc: InsuranceDocument) => {
      if (!canAccessDocuments) {
        message.warning("You don't have permission to preview documents");
        return;
      }
      
      try {
        const response = await axiosInstance.get(
          `/oandm/insurance/preview/${partyId}/${doc.id}`,
          {
            responseType: 'blob'
          }
        );

        const blob = new Blob([response.data], { 
          type: doc.filemimetype || 'application/pdf' 
        });
        const blobUrl = window.URL.createObjectURL(blob);
        const newWindow = window.open(blobUrl, '_blank');
        
        if (newWindow) {
          newWindow.onload = () => {
            setTimeout(() => {
              window.URL.revokeObjectURL(blobUrl);
            }, 100);
          };
        }
      } catch (error: any) {
        console.error("Failed to preview document:", error);
        message.error("Failed to preview document");
      }
    };

    Modal.info({
      title: (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 8,
          fontSize: isMobile ? '16px' : '18px',
          fontWeight: 600
        }}>
          <FileTextOutlined style={{ color: themeTokens.primaryColor }} />
          <span>Insurance Documents ({insuranceDocuments.length})</span>
        </div>
      ),
      width: isMobile ? '95%' : 800,
      style: { top: 20 },
      icon: null,
      closable: true,
      okText: 'Close',
      okButtonProps: { 
        size: 'large',
        style: { borderRadius: '8px' }
      },
      content: (
        <div style={{ maxHeight: '60vh', overflowY: 'auto', marginTop: 16 }}>
          {insuranceDocuments.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: themeTokens.textColorSecondary
            }}>
              <FileTextOutlined style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
              <div style={{ fontSize: '16px', marginBottom: '8px' }}>No Documents Available</div>
              <div style={{ fontSize: '14px' }}>There are no insurance documents uploaded for this party yet.</div>
            </div>
          ) : (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {insuranceDocuments.map((doc, index) => (
              <div
                key={doc.id}
                style={{
                  padding: isMobile ? '12px' : '16px',
                  border: `1px solid ${themeTokens.borderColor}`,
                  borderRadius: '8px',
                  background: themeTokens.contentBackground,
                  transition: 'all 0.3s ease',
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 12
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 8,
                      marginBottom: 8
                    }}>
                      <span style={{ fontSize: '20px' }}>
                        {getFileIcon(doc.file_extension)}
                      </span>
                      <Text
                        strong
                        style={{
                          fontSize: isMobile ? '13px' : '14px',
                          color: themeTokens.textColor,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {doc.document_name || doc.original_filename}
                      </Text>
                    </div>
                    
                    <div style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap',
                      gap: isMobile ? 6 : 8,
                      fontSize: isMobile ? '11px' : '12px',
                      color: themeTokens.textColorSecondary
                    }}>
                      <span>
                        {doc.original_filename}
                      </span>
                      {doc.file_extension && (
                        <>
                          <span>•</span>
                          <span style={{ 
                            color: getFileColor(doc.file_extension),
                            fontWeight: 500
                          }}>
                            {doc.file_extension.toUpperCase()}
                          </span>
                        </>
                      )}
                      {doc.filesize && (
                        <>
                          <span>•</span>
                          <span>
                            {(doc.filesize / 1024).toFixed(2)} KB
                          </span>
                        </>
                      )}
                      {doc.created_at && (
                        <>
                          <span>•</span>
                          <span>
                            {dayjs(doc.created_at).format('DD MMM YYYY')}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {canAccessDocuments && (
                    <Space size="small">
                      <Tooltip title="Preview">
                        <Button
                          type="text"
                          size="small"
                          icon={<FileTextOutlined />}
                          onClick={() => handlePreview(doc)}
                          style={{
                            color: themeTokens.primaryColor
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Download">
                        <Button
                          type="primary"
                          size="small"
                          icon={<DownloadOutlined />}
                          onClick={() => handleDownload(doc)}
                        />
                      </Tooltip>
                    </Space>
                  )}
                </div>
              </div>
            ))}
            </Space>
          )}
        </div>
      ),
      maskClosable: true,
      centered: true
    });
  };

  const handleInsuranceRenew = async () => {
    if (!renewDate) {
      message.warning("Please select the new insurance expiry date");
      return;
    }

    try {
      setRenewLoading(true);

      // Upload documents first if any
      for (const file of renewFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('partyId', partyId);
        formData.append('documentName', file.name);
        await axiosInstance.post('/oandm/insurance/renew/upload', formData);
      }

      // Update the insurance end date
      await axiosInstance.post('/oandm/insurance/renew', {
        partyId,
        insurance_end_date: renewDate.format("YYYY-MM-DD"),
      });

      message.success("Insurance renewed successfully");
      setRenewModalVisible(false);
      setRenewDate(null);
      setRenewFiles([]);
      fetchInsuranceData();
    } catch (error: any) {
      console.error("Failed to renew insurance:", error);
      message.error(error?.response?.data?.message || "Failed to renew insurance");
    } finally {
      setRenewLoading(false);
    }
  };

  const layoutStyles = useMemo(
    () => ({
      backgroundColor: themeTokens.background,
      minHeight: "100vh",
      height: "100%",
      overflow: "hidden",
      display: "flex",
      color: themeTokens.textColor,
      transition: "all 0.3s ease-in-out",
    }),
    [themeTokens]
  );

  const contentStyles = useMemo(
    () => ({
      marginLeft: isMobile ? 0 : isNavMenuClose ? 80 : 240,
      width: "100%",
      padding: isMobile ? "56px 16px 16px" : "0",
      overflow: "auto",
      minHeight: "100vh",
      background: themeTokens.contentBackground,
      transition: "all 0.3s ease-in-out",
      color: themeTokens.textColor,
    }),
    [themeTokens, isNavMenuClose, isMobile]
  );

  const headerStyles = useMemo(
    () => ({
      background: "transparent",
      padding: isMobile ? "8px 12px" : "8px 16px",
      height: 52,
      lineHeight: "52px",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end" as const,
      color: themeTokens.textColor,
      backdropFilter: "none",
    }),
    [isMobile, themeTokens.textColor]
  );

  useEffect(() => {
    document.documentElement.setAttribute(
      "data-theme",
      isDarkMode ? "dark" : "light"
    );
    document.body.style.backgroundColor = themeTokens.background;
    document.body.style.color = themeTokens.textColor;

    const style = document.createElement("style");
    style.textContent = `
      :root {
        color-scheme: ${isDarkMode ? "dark" : "light"};
        --primary-color: ${themeTokens.primaryColor};
        --background-color: ${themeTokens.background};
        --content-background: ${themeTokens.contentBackground};
        --text-color: ${themeTokens.textColor};
        --text-color-secondary: ${themeTokens.textColorSecondary};
        --border-color: ${themeTokens.borderColor};
      }

      @keyframes insuranceGlow {
        from {
          text-shadow: 0 0 4px rgba(82, 196, 26, 0.4), 0 0 10px rgba(82, 196, 26, 0.2);
        }
        to {
          text-shadow: 0 0 10px rgba(82, 196, 26, 0.8), 0 0 20px rgba(82, 196, 26, 0.4);
        }
      }

      @keyframes insuranceGlowRed {
        from {
          text-shadow: 0 0 4px rgba(255, 77, 79, 0.4), 0 0 10px rgba(255, 77, 79, 0.2);
        }
        to {
          text-shadow: 0 0 10px rgba(255, 77, 79, 0.8), 0 0 20px rgba(255, 77, 79, 0.4);
        }
      }

      .insurance-expiry-date {
        color: #52c41a !important;
        animation: insuranceGlow 3s ease-in-out infinite alternate;
        transition: color 0.3s ease;
        font-weight: 700;
      }

      .insurance-expiry-date-red {
        color: #ff4d4f !important;
        animation: insuranceGlowRed 3s ease-in-out infinite alternate;
        transition: color 0.3s ease;
        font-weight: 700;
      }

    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [isDarkMode, themeTokens]);

  return (
    <Layout style={layoutStyles}>
      <ClientNavigation />
      <Layout style={contentStyles}>
        <Layout.Header style={headerStyles}>
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "space-between",
            width: "100%"
          }}>
            {/* Left side - Party Name */}
            <div style={{ 
              fontWeight: 600, 
              fontSize: isMobile ? "16px" : "23px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}>
              {partyName || ""}
            </div>
            
            {/* Right side - Insurance, Notifications, Logout, and Username */}
            <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 12 }}>
              {/* Insurance End Date and Download Button - Only show if user has permission */}
              {canViewInsurance && insuranceEndDate && (
                <>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8
                  }}>
                    <Text strong style={{ 
                      fontSize: isMobile ? "14px" : "23px",
                      color: "#000000",
                      whiteSpace: "nowrap",
                      fontWeight: 800
                    }}>
                      Insurance Exp: <strong className={insuranceExpiringSoon ? "insurance-expiry-date-red" : "insurance-expiry-date"}>{insuranceEndDate}</strong>
                    </Text>
                    {canAccessDocuments && (
                      <Tooltip title="Download Insurance Documents">
                        <Button
                          type="text"
                          shape="circle"
                          size="small"
                          icon={<DownloadOutlined />}
                          onClick={handleInsuranceDownload}
                          loading={loadingInsurance}
                          disabled={insuranceDocuments.length === 0}
                          style={{
                            opacity: insuranceDocuments.length === 0 ? 0.5 : 1
                          }}
                        />
                      </Tooltip>
                    )}
                    {canAccessDocuments && (
                      <Tooltip title="Insurance Renewed">
                        <Button
                          type="text"
                          shape="circle"
                          size="small"
                          icon={<CheckCircleOutlined />}
                          onClick={() => setRenewModalVisible(true)}
                          style={{
                            color: themeTokens.primaryColor
                          }}
                        />
                      </Tooltip>
                    )}
                  </div>
                  <div style={{
                    height: 24,
                    width: 2,
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)',
                    borderRadius: '1px',
                    margin: '0 4px'
                  }} />
                </>
              )}
              
              {/* Notifications, Logout, Username */}
              {/* Notifications icon hidden */}
              {/* <Tooltip title="Notifications">
                <Badge count={unreadCount} size="small" offset={[-2, 2]}>
                  <Button
                    type="text"
                    shape="circle"
                    size="small"
                    icon={<BellOutlined />}
                    onClick={handleNotificationClick}
                  />
                </Badge>
              </Tooltip> */}
              <Tooltip title="Logout">
                <Button
                  type="text"
                  shape="circle"
                  size="small"
                  icon={<LogoutOutlined />}
                  onClick={logout}
                />
              </Tooltip>
              <Tooltip title={user?.email || ""}>
                <div style={{ 
                  fontWeight: 600, 
                  cursor: "default",
                  fontSize: isMobile ? "14px" : "23px"
                }}>
                  {user?.name || "User"}
                </div>
              </Tooltip>
            </div>
          </div>
        </Layout.Header>
        <Layout.Content>
          {permissionsLoading ? <PageLoader /> : <Outlet />}
        </Layout.Content>
      </Layout>

      {/* Notification Panel - disabled */}
      {/* <NotificationPanel
        visible={notificationPanelVisible}
        onClose={handleNotificationPanelClose}
      /> */}

      {/* Insurance Renewal Modal */}
      <Modal
        title="Renew Insurance"
        open={renewModalVisible}
        onCancel={() => {
          setRenewModalVisible(false);
          setRenewDate(null);
          setRenewFiles([]);
        }}
        onOk={handleInsuranceRenew}
        confirmLoading={renewLoading}
        okText="Renew"
        centered
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>New Insurance Expiry Date *</Text>
            <DatePicker
              value={renewDate}
              onChange={(date) => setRenewDate(date)}
              style={{ width: '100%' }}
              format="DD MMM YYYY"
              placeholder="Select new expiry date"
            />
          </div>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Upload Documents (optional)</Text>
            <Upload
              multiple
              beforeUpload={(file) => {
                setRenewFiles((prev) => [...prev, file]);
                return false;
              }}
              onRemove={(file) => {
                setRenewFiles((prev) => prev.filter((f) => f.uid !== file.uid));
              }}
              fileList={renewFiles.map((f) => ({
                uid: f.uid || f.name,
                name: f.name,
                status: 'done' as const,
              }))}
            >
              <Button icon={<UploadOutlined />}>Select Files</Button>
            </Upload>
          </div>
        </Space>
      </Modal>
    </Layout>
  );
};

const ClientLayout: React.FC = () => {
  return (
    <ClientLayoutInner />
  );
};

export default React.memo(ClientLayout);
